"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import AnnouncementBarV2 from "./AnnouncementBarV2";
import LogoV2 from "./LogoV2";
import SearchBarV2 from "./SearchBarV2";
import UserActionsV2 from "./UserActionsV2";
import NavigationBarV2 from "./NavigationBarV2";
import SideMenuV2 from "./SideMenuV2";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";

interface HeaderV2ContainerProps {
  showTopBar?: number;
  showPromotional?: number;
}

import { HeaderCacheProvider, useHeaderCache } from "./HeaderCacheContext";
import Link from "next/link";

export default function HeaderV2Container(props: HeaderV2ContainerProps) {
  return (
    <HeaderCacheProvider>
      <HeaderV2ContainerContent {...props} />
    </HeaderCacheProvider>
  );
}

function HeaderV2ContainerContent({
  showTopBar = 1,
}: HeaderV2ContainerProps) {
  const { colors, spacing } = HEADER_V2_DESIGN_SYSTEM;
  const [activeOverlay, setActiveOverlay] = useState<"search" | "megamenu" | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { categories } = useHeaderCache();


  useEffect(() => {
    console.log("active overlay changed:", activeOverlay);
  }, [activeOverlay]);
  return (
    <header className="w-full flex flex-col font-sans">
      {/* 1. Announcement Bar (Fluid outer wrapper) */}
      {showTopBar === 1 && <AnnouncementBarV2 />}

      {/* 2. Main Action Bar Row (Responsive Flex Grid) */}
      <div className={`${colors.whiteBg} ${colors.neutralBorder} border-b py-3.5 md:py-5 w-full`}>
        <div className={`${spacing.containerPadding} max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-3.5 md:gap-8`}>
          
          {/* Mobile Top Row: Hamburger Menu (Left) + Logo (Center) + Cart/Actions (Right) */}
          <div className="flex items-center justify-between w-full md:w-auto gap-4">
            {/* Hamburger Button (visible < md) */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-1.5 -ml-1.5 rounded-md hover:bg-slate-100/50 text-slate-700 cursor-pointer"
              aria-label="Open mobile navigation drawer"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <LogoV2 />

            {/* Mobile Actions Overlay (visible < md) */}
            <div className="md:hidden flex items-center">
              <UserActionsV2 />
            </div>
          </div>

          {/* Search Bar Row: Full width on mobile, centered flex-grow on desktop */}
          <div className="w-full md:flex-1 md:max-w-[760px]">
            <SearchBarV2
              isOpen={activeOverlay === "search"}
              onOpenChange={(open) => setActiveOverlay(open ? "search" : null)}
            />
          </div>

          {/* Desktop User Actions V2 (hidden < md) */}
          <div className="hidden md:flex items-center">
            <UserActionsV2 />
          </div>

        </div>
      </div>

      {/* 3. Mobile Horizontal Scrollable Categories Bar (visible < md) */}
      {categories.length > 0 && (
        <div className={`md:hidden w-full ${colors.whiteBg} border-b border-slate-100 overflow-x-auto whitespace-nowrap py-2.5 px-4 flex gap-4 text-xs font-semibold text-slate-600 scrollbar-none`}>
          <Link href="/search-grid/best-sellers" className="hover:text-blue-600">
            All
          </Link>
          {categories.slice(0, 8).map((cat) => (
            <Link key={cat.id} href={cat.href} className="hover:text-blue-600 capitalize">
              {cat.label}
            </Link>
          ))}
        </div>
      )}

      {/* 4. Desktop Navigation Bar V2 (hidden < md) */}
      <NavigationBarV2
        isMenuOpen={activeOverlay === "megamenu"}
        // onMenuOpenChange={(open) => setActiveOverlay(open ? "megamenu" : null)}3
        onMenuOpenChange={(open) => setActiveOverlay(open ? "megamenu" : null)}
      />

      {/* 5. Mobile Side Drawer Accordion overlay */}
      <SideMenuV2 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </header>
  );
}
